import React, { useState, useEffect } from "react";
import { Button, SelectBox, TagBox } from "devextreme-react";

import QuarterlyPeriodsList from "./QuarterlyPeriodList";
import { RunSurchargeIncreaseApi } from "@/shared/api/RunSurchargeIncrease.api";
import { ProductLineSelectList } from "@/shared/business/FormulaSimulator";
import { Constants, Pages } from "@/shared/constants";
import { SurchargeEvent } from "@/shared/models/SurchargeEvent";
import { DialogType, SurchargeEventTypeCode } from "@/shared/enums";
import NextButton from "@/components/NextButton";
import { Utilities } from "@/shared/utils/utilities";
import { SetLastestAction } from "@/shared/business/ReviewNewCharges";
import { store } from "@/store/store";
import { setDialogInfo, setToastInfo } from "@/redux/shareSlice";
import { DialogInfo } from "@/shared/models/DialogInfo";

const RunSurchargeIncrease: React.FC = () => {
  // **State Management**
  const [activeTab, setActiveTab] = useState(SurchargeEventTypeCode.Monthly);

  const [monthlyEvents, setMonthlyEvents] = useState<SurchargeEvent[]>([]);
  const [quarterlyEvents, setQuarterlyEvents] = useState<SurchargeEvent[]>([]);

  const [selectedMonth, setSelectedMonth] = useState<string>("");
  const [selectedQuarter, setSelectedQuarter] = useState<string>("");
  const [surchargeType, setSurchargeType] = useState<string>("");
  const [updateDay, setUpdateDay] = useState<string>("1");
  const [productType, setProductType] = useState<string[]>([]);
  const [selectedQuarterDetails, setSelectedQuarterDetails] = useState<any>(null);
  const [isDisableSurchargeButton, setIsDisableSurchargeButton] = useState<boolean>(false);

  // **Fetch Monthly & Quarterly Events**
  useEffect(() => {
    const fetchData = async () => {
      const allEvents = await RunSurchargeIncreaseApi.tryListSurchargeEvents();
      if (!allEvents) {
        return;
      }

      var today = new Date();
      var currentMonth = today.getMonth();
      var currentYear = today.getFullYear();
      const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
      const fourMonthsOut = new Date(today.getFullYear(), today.getMonth() + 4, 1);

      const monthly = allEvents.filter((item) => item.type.code === SurchargeEventTypeCode.Monthly);
      const quarterly = allEvents
        .filter((item) => item.type.code === SurchargeEventTypeCode.Quarterly)
        .filter((item) => {
          var itemMonth = new Date(item.earliestDate);
          return sixMonthsAgo <= itemMonth && itemMonth <= fourMonthsOut;
        });

      setMonthlyEvents(monthly);
      setQuarterlyEvents(quarterly);

      // **Set Default Values**
      if (monthly.length > 0) {
        var filteredMonth = monthly.filter((item) => {
          var itemMonth = new Date(item.earliestDate);
          return itemMonth.getMonth() === currentMonth && itemMonth.getFullYear() === currentYear;
        });
        setSelectedMonth(filteredMonth.length > 0 ? filteredMonth[0].id : monthly[0].id);
      }

      if (quarterly.length > 0) {
        setSelectedQuarter(quarterly[0].id);
        setSelectedQuarterDetails(quarterly[0]);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    let quarterlyEventDet = quarterlyEvents.filter((x) => x.id === selectedQuarter)[0];
    setSelectedQuarterDetails(quarterlyEventDet);
  }, [selectedQuarter]);

  const handleRunCalculation = async () => {
    if (Utilities.isNullOrEmptyString(surchargeType) || productType.length === 0) {
      store.dispatch(setToastInfo("Surcharge type and product line can not be empty."));
      return;
    }

    await calculate(activeTab !== SurchargeEventTypeCode.Monthly);
  };

  const calculate = async (bypassQuarterlyApprovalCheck: boolean) => {
    let selectedEventID = "";
    let selectedEventName = "";
    if (activeTab === SurchargeEventTypeCode.Monthly) {
      selectedEventID = selectedMonth;
      selectedEventName = monthlyEvents.filter((item) => item.id === selectedEventID)[0].name;
    } else {
      selectedEventID = selectedQuarter;
      selectedEventName = quarterlyEvents.filter((item) => item.id === selectedEventID)[0].name;
    }

    var bypassArgument = bypassQuarterlyApprovalCheck ? "&bypassQuarterlyApprovalCheck=true" : "";
    SetLastestAction(selectedEventName, Number.parseInt(updateDay));
    var response = await RunSurchargeIncreaseApi.tryGetCalculateSurcharge(selectedEventID, updateDay, surchargeType, productType, bypassArgument);
    if (response === undefined) {
      return;
    }

    if (response.status === 200) {
      setIsDisableSurchargeButton(true);
      store.dispatch(setToastInfo("Calculation Complete\r\n\r\nCalculations are complete.  Please proceed to the next step to approve surcharges."));
    } else {
      showWaningDialog(response.data);
    }
  };

  const showWaningDialog = (responseData: string) => {
    if (responseData && responseData.startsWith("NO-APPROVED-RECORD")) {
      store.dispatch(
        setDialogInfo({
          isShow: true,
          title: "No Quarterly Run Approved",
          dialogType: DialogType.warning,
          okText: "Continue Without Quarterly Results",
          cancelText: "Cancel And Return",
          okAction: async () => await calculate(true),
          content:
            "<p>There is no previously approved quarterly run to use in calulating here.&nbsp;&nbsp;" +
            "If you continue without quarterly results, you may get the wrong values in calculation.</p>" +
            '<p>Clicking "Continue Without Quarterly Results" will perform the calculation as-is without quarterly values applied.</p>' +
            '<p>Clicking "Cancel And Return" will return you to the previous screen without doing anything.</p>',
        } as DialogInfo)
      );
    } else {
      store.dispatch(
        setDialogInfo({
          isShow: true,
          title: "Calculation Failure",
          dialogType: DialogType.error,
          content: responseData,
        } as DialogInfo)
      );
    }
  };

  return (
    <>
      <h4>Run Surcharge Increase</h4>
      <h5>Calculation Types</h5>

      {/* <TabPanel
        height={260}
        dataSource={["MONTHLY","QUARTERLY"]}
        selectedIndex={selectedIndex}
        onOptionChanged={onSelectionChanged}
        loop={true}
        // itemTitleRender={itemTitleRender}
        // itemComponent={CompanyItem}
        animationEnabled={true}
        swipeEnabled={true}
      /> */}

      {/* **Tab Navigation Using Buttons** */}
      <div
        style={{
          display: "flex",
          gap: "10px",
          borderBottom: "2px solid #ccc",
          paddingBottom: "10px",
        }}
      >
        <button
          className={activeTab === SurchargeEventTypeCode.Monthly ? "active-tab customButton" : ""}
          onClick={() => setActiveTab(SurchargeEventTypeCode.Monthly)}
          style={{
            padding: "10px",
            background: activeTab === SurchargeEventTypeCode.Monthly ? "#009A49" : "#f0f0f0",
            border: "none",
            cursor: "pointer",
            color: activeTab === SurchargeEventTypeCode.Monthly ? "#fff" : "#000",
          }}
        >
          MONTHLY
        </button>
        <button
          className={activeTab === SurchargeEventTypeCode.Quarterly ? "active-tab customButton" : ""}
          onClick={() => setActiveTab(SurchargeEventTypeCode.Quarterly)}
          style={{
            padding: "10px",
            background: activeTab === SurchargeEventTypeCode.Quarterly ? "#009A49" : "#f0f0f0",
            border: "none",
            cursor: "pointer",
            color: activeTab === SurchargeEventTypeCode.Quarterly ? "#fff" : "#000",
          }}
        >
          QUARTERLY
        </button>
      </div>

      {/* **Only One Parent Div for Each Tab Content** */}
      <div className="container" style={{ marginTop: "20px" }}>
        <div className="row">
          {activeTab === SurchargeEventTypeCode.Monthly ? (
            <div className="col-6">
              <div style={{ marginBottom: "15px" }}>
                <SelectBox
                  id="SelectMonth"
                  label="Selected Month:"
                  value={selectedMonth}
                  dataSource={monthlyEvents}
                  displayExpr="name"
                  valueExpr="id"
                  onValueChange={(e) => setSelectedMonth(e)}
                ></SelectBox>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <SelectBox
                  label="Update day:"
                  value={updateDay}
                  dataSource={Constants.SurchargeDays}
                  displayExpr="name"
                  valueExpr="id"
                  onValueChange={(e) => setUpdateDay(e)}
                ></SelectBox>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <SelectBox
                  label="Surcharge Type:"
                  value={surchargeType}
                  dataSource={Constants.SurchargeTypes}
                  onValueChange={(e) => setSurchargeType(e)}
                ></SelectBox>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <TagBox
                  label="Product Line:"
                  value={productType}
                  dataSource={ProductLineSelectList}
                  showSelectionControls={true}
                  onValueChanged={(e) => setProductType(e.value)}
                ></TagBox>
              </div>
            </div>
          ) : (
            <div className="col-6">
              <div style={{ marginBottom: "15px" }}>
                <SelectBox
                  id="SelectQuarter"
                  label="Selected Quarter:"
                  value={selectedQuarter}
                  dataSource={quarterlyEvents}
                  displayExpr="name"
                  valueExpr="id"
                  onValueChange={(e) => setSelectedQuarter(e)}
                ></SelectBox>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <label>Months in Scope:</label>
                <QuarterlyPeriodsList periods={selectedQuarterDetails?.periods || []} />
              </div>

              <div style={{ marginBottom: "15px" }}>
                <SelectBox
                  label="Surcharge Type:"
                  value={surchargeType}
                  dataSource={Constants.SurchargeTypes}
                  onValueChange={(e) => setSurchargeType(e)}
                ></SelectBox>
              </div>

              <div style={{ marginBottom: "15px" }}>
                <TagBox
                  label="Product Line:"
                  value={productType}
                  dataSource={ProductLineSelectList}
                  showSelectionControls={true}
                  onValueChange={(e) => setProductType(e)}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* **Run Button** */}
      <div style={{ marginTop: "20px" }}>
        <Button type="default" className="me-2" onClick={handleRunCalculation} disabled={isDisableSurchargeButton ? true : false}>
          RUN INCREASE
        </Button>

        <NextButton nextPage={Pages.ReviewNewCharges} />
      </div>
    </>
  );
};

export default RunSurchargeIncrease;
